import { useState, useEffect, useRef } from 'react';
import { X, Search, CheckCircle, Shield, MapPin, Phone, Hash, Loader } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function FflDealerModal({ isOpen, onClose, onSelect, selectedDealer }) {
  const [zip, setZip] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [zipError, setZipError] = useState('');
  const inputRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const doSearch = async (searchZip, searchQ) => {
    setLoading(true);
    setZipError('');
    try {
      const params = new URLSearchParams();
      if (searchZip) params.set('zip', searchZip);
      if (searchQ) params.set('q', searchQ);
      params.set('limit', '15');
      const res = await fetch(`${BASE}/api/ffl/search?${params}`);
      const data = await res.json();
      setResults(Array.isArray(data) ? data : []);
      setSearched(true);
    } catch (e) {
      setResults([]);
      setSearched(true);
    }
    setLoading(false);
  };

  const handleZipSearch = (e) => {
    e.preventDefault();
    if (!zip.trim() || zip.trim().length < 5) {
      setZipError('Please enter a valid 5-digit ZIP code');
      return;
    }
    doSearch(zip.trim(), '');
  };

  const handleNameSearch = (e) => {
    e.preventDefault();
    if (query.trim().length < 2) return;
    doSearch('', query.trim());
  };

  const handleSelect = (dealer) => {
    onSelect({
      dealer_id: dealer.lic_number || '',
      business_name: dealer.business_name || '',
      address: dealer.address || '',
      city: dealer.city || '',
      state: dealer.state || '',
      zip: dealer.zip || '',
      phone: dealer.phone || '',
      license_number: dealer.lic_number || '',
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111111] border border-white/10 w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
          <div>
            <h3 className="font-bebas text-2xl text-white tracking-wider flex items-center gap-2">
              <Shield size={18} className="text-orange-500" />
              FFL DEALER FINDER
            </h3>
            <p className="font-inter text-xs text-zinc-500 mt-0.5">
              Texas SOT-eligible dealers — search by ZIP code or dealer name
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors p-1">
            <X size={20} />
          </button>
        </div>

        {/* Search inputs */}
        <div className="px-5 py-4 border-b border-white/10 flex-shrink-0 space-y-3">
          {/* ZIP search */}
          <form onSubmit={handleZipSearch} className="flex gap-2">
            <div className="flex-1">
              <input
                ref={inputRef}
                data-testid="ffl-zip-input"
                type="text"
                value={zip}
                onChange={e => { setZip(e.target.value); setZipError(''); }}
                placeholder="Search by ZIP code (e.g. 79416)"
                maxLength={5}
                className="w-full bg-[#1a1a1a] border border-white/10 focus:border-orange-500 text-white font-mono text-sm px-4 py-2.5 outline-none transition-colors placeholder-zinc-600"
              />
              {zipError && <p className="font-inter text-xs text-red-400 mt-1">{zipError}</p>}
            </div>
            <button type="submit" disabled={loading}
              data-testid="ffl-zip-search-btn"
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-base tracking-widest px-4 py-2.5 transition-colors whitespace-nowrap">
              <MapPin size={14} />FIND NEAR ME
            </button>
          </form>

          {/* Name/city search */}
          <form onSubmit={handleNameSearch} className="flex gap-2">
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Or search by dealer name or city"
              className="flex-1 bg-[#1a1a1a] border border-white/10 focus:border-white/30 text-white font-inter text-sm px-4 py-2.5 outline-none transition-colors placeholder-zinc-600"
            />
            <button type="submit" disabled={loading || query.trim().length < 2}
              className="flex items-center gap-2 border border-white/20 hover:border-white/40 disabled:opacity-40 text-zinc-400 hover:text-white font-inter text-xs px-4 py-2.5 transition-colors whitespace-nowrap">
              <Search size={13} />Search
            </button>
          </form>
        </div>

        {/* Currently selected */}
        {selectedDealer?.business_name && (
          <div className="px-5 py-3 bg-orange-500/8 border-b border-orange-500/20 flex-shrink-0">
            <div className="flex items-center gap-2">
              <CheckCircle size={14} className="text-green-400 flex-shrink-0" />
              <div>
                <p className="font-inter text-xs text-zinc-500 uppercase tracking-wider">Currently Selected</p>
                <p className="font-inter text-sm text-white font-semibold">{selectedDealer.business_name}
                  <span className="font-normal text-zinc-400 ml-2">— {selectedDealer.city}, {selectedDealer.state}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-3 text-zinc-500">
              <Loader size={20} className="animate-spin text-orange-500" />
              <span className="font-inter text-sm">Searching dealers...</span>
            </div>
          ) : !searched ? (
            <div className="text-center py-10 px-6">
              <MapPin size={32} className="text-zinc-700 mx-auto mb-3" />
              <p className="font-inter text-sm text-zinc-500">Enter your ZIP code to find licensed FFL dealers near you</p>
              <p className="font-inter text-xs text-zinc-700 mt-2">
                Showing Texas dealers. Only Type 01/07 FFLs eligible for NFA suppressor transfers.
              </p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-10 px-6">
              <Search size={28} className="text-zinc-700 mx-auto mb-3" />
              <p className="font-inter text-sm text-zinc-400">No dealers found for that search</p>
              <p className="font-inter text-xs text-zinc-600 mt-1">Try a different ZIP code or search by city name</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {results.map((dealer, i) => (
                <button
                  key={dealer.lic_number || i}
                  data-testid={`ffl-dealer-result-${i}`}
                  onClick={() => handleSelect(dealer)}
                  className="w-full text-left px-5 py-4 hover:bg-white/3 transition-colors group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-inter text-sm font-semibold text-white group-hover:text-orange-400 transition-colors">
                          {dealer.business_name}
                        </span>
                        {dealer.lic_type === '07' && (
                          <span className="font-inter text-[9px] text-purple-400 border border-purple-500/30 bg-purple-500/10 px-1.5 py-0.5 uppercase tracking-wider">Manufacturer</span>
                        )}
                        <span className="font-inter text-[9px] text-orange-400 border border-orange-500/20 bg-orange-500/8 px-1.5 py-0.5 uppercase tracking-wider">NFA Capable</span>
                      </div>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <MapPin size={10} className="text-zinc-600 flex-shrink-0" />
                        <p className="font-inter text-xs text-zinc-400">
                          {dealer.address}, {dealer.city}, {dealer.state} {dealer.zip}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {dealer.phone && (
                          <div className="flex items-center gap-1">
                            <Phone size={9} className="text-zinc-600" />
                            <span className="font-inter text-[10px] text-zinc-500">{dealer.phone}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Hash size={9} className="text-zinc-600" />
                          <span className="font-inter text-[10px] text-zinc-600">{dealer.lic_number}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      {dealer.distance_miles != null ? (
                        <p className="font-bebas text-base text-orange-400 tracking-wider">{dealer.distance_miles} mi</p>
                      ) : null}
                      <p className="font-inter text-[10px] text-zinc-600 group-hover:text-orange-400 transition-colors mt-1">
                        Select →
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/10 flex-shrink-0">
          <div className="flex items-center justify-between">
            <p className="font-inter text-[10px] text-zinc-600">
              Please confirm with your dealer that they hold SOT Class 3 status and accept NFA suppressor transfers.
            </p>
            <button onClick={onClose} className="font-inter text-xs text-zinc-500 hover:text-white transition-colors ml-4 whitespace-nowrap">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
